import {SupportedLocales} from '#types/locale.js';

export const studioTasksLanguage = {
  [SupportedLocales.EN]: {
    activeTab: 'Active',
    actionsForTask: (name: string) => `Actions for ${name}`,
    archiveTask: 'Archive',
    archiveTaskConfirmationConfirm: 'Archive task',
    archiveTaskConfirmationDescription: (name: string) =>
      `This will stop future runs for "${name}". You can unarchive it later to resume the schedule.`,
    archiveTaskConfirmationTitle: 'Archive scheduled task?',
    archivedTab: 'Archived',
    cancel: 'Cancel',
    cardSchedule: {
      daily: (time: string) => `Daily at ${time}`,
      monthly: (day: string, time: string) => `${day} at ${time}`,
      weekdays: (time: string) => `Every weekday at ${time}`,
      weekly: (day: string, time: string) => `Every ${day} at ${time}`,
    },
    createTask: 'Create task',
    createTaskFailed: 'Scheduled task could not be created.',
    createdBy: (name: string) => `Created by ${name}`,
    datasetGroup: 'Dataset group',
    day: 'Day',
    delivery: 'Delivery',
    deliveryAiStudio: 'Run in AI Studio',
    deliveryAiStudioAndEmail: (email: string) =>
      `Run in AI Studio and email ${email}`,
    editTask: 'Edit',
    editTaskAction: 'Edit task',
    emailResultsHelper: 'Leave blank to keep results in AI Studio only.',
    emailResultsTo: 'Email results to',
    frequency: 'Frequency',
    inTimezone: (value: string, timezone: string) =>
      value.endsWith('.')
        ? `${value.slice(0, -1)} ${timezone}.`
        : `${value} ${timezone}`,
    loadDatasetGroupsFailed: 'Scheduled tasks could not load dataset groups.',
    loadTasksFailed: 'Scheduled tasks could not be loaded.',
    loadingRuns: 'Loading task runs',
    loadingScheduledTasks: 'Loading scheduled tasks',
    manualRun: 'Manual',
    never: 'Never',
    nextPage: 'Next',
    noDatasetGroups:
      'Create an approved dataset group before scheduling Studio tasks.',
    noActiveTaskSearchResults: 'No active tasks match your query.',
    noArchivedTasks:
      'No archived tasks. Archiving a task stops its schedule but keeps its run history.',
    noArchivedTaskSearchResults: 'No archived tasks match your query.',
    noTasks: 'No scheduled tasks yet.',
    pagination: (page: number, pageCount: number) =>
      `Page ${page} of ${pageCount}`,
    pauseTask: 'Pause',
    persistSuggestions: 'Save audience suggestions from each run',
    persistSuggestionsHelper:
      'When Marve returns valid audience suggestions, save them in AI Studio for review and activation.',
    previousPage: 'Previous',
    prompt: 'Prompt',
    promptPlaceholder:
      'Describe what should run on each schedule (e.g. "Find new high-value audience opportunities and explain why they are worth activating.")',
    resumeTask: 'Resume',
    resumeTaskAction: 'Resume task',
    runStatusLabels: {
      cancelled: 'Cancelled',
      failed: 'Failed',
      queued: 'Queued',
      running: 'Running',
      succeeded: 'Succeeded',
      timed_out: 'Timed out',
    },
    runNowTask: 'Run now',
    runNowTaskFailed: 'Scheduled task could not be run.',
    runPending: 'Run pending',
    runResultPending: 'Run in progress.',
    ranAt: 'Run',
    runHistory: 'Run history',
    result: 'Result',
    schedule: 'Schedule',
    scheduledTasks: 'Scheduled Tasks',
    searchTasks: 'Search tasks',
    taskName: 'Task name',
    taskStatusTabs: 'Scheduled task status',
    noRunResult: 'No result summary.',
    noRuns: 'No runs yet.',
    noSuccessRate: 'No completed runs yet.',
    nextRunAt: (date: string, time: string) => `Next run on ${date} at ${time}`,
    noNextRun: 'No next run scheduled',
    pauseTaskAction: 'Pause task',
    status: 'Status',
    successRate: (rate: number, count: number) =>
      `${rate}% of last ${count} ${count === 1 ? 'run' : 'runs'}`,
    successRateLabel: 'Success rate',
    tableColumns: {
      actions: 'Task actions',
      lastRun: 'Last Run',
      prompt: 'Prompt',
      schedule: 'Schedule',
      taskName: 'Task Name',
    },
    time: 'Time',
    timezone: 'Timezone',
    tryAgain: 'Try again',
    unknownCreator: 'Unknown',
    unarchiveTask: 'Unarchive',
    updateTask: 'Update task',
    updateTaskFailed: 'Scheduled task could not be updated.',
    viewRunHistory: 'View run history',
    frequencyOptions: {
      hourly: 'Hourly',
      daily: 'Daily',
      weekdays: 'Every weekday',
      weekly: 'Weekly',
      monthly: 'Monthly',
      cron: 'Custom',
      custom: 'Custom',
    },
    monthlyDay: 'Monthly day',
    monthlyOptions: {
      firstDay: '1st of the month',
      fifteenthDay: '15th of the month',
    },
    scheduleSummary: {
      daily: (time: string) => `Runs every day at ${time}.`,
      monthly: (day: string, time: string) => `Runs on the ${day} at ${time}.`,
      weekdays: (time: string) => `Runs every weekday at ${time}.`,
      weekly: (day: string, time: string) => `Runs every ${day} at ${time}.`,
    },
    statusLabels: {
      active: 'Active',
      archived: 'Archived',
      paused: 'Paused',
    },
    startsOn: 'Starts on',
    weekdayOptions: {
      MO: 'Monday',
      TU: 'Tuesday',
      WE: 'Wednesday',
      TH: 'Thursday',
      FR: 'Friday',
      SA: 'Saturday',
      SU: 'Sunday',
    },
  },
  [SupportedLocales.ES]: {
    activeTab: 'Activas',
    actionsForTask: (name: string) => `Acciones para ${name}`,
    archiveTask: 'Archivar',
    archiveTaskConfirmationConfirm: 'Archivar tarea',
    archiveTaskConfirmationDescription: (name: string) =>
      `Esto detendrá las ejecuciones futuras de "${name}". Puedes desarchivarla más adelante para reanudar la programación.`,
    archiveTaskConfirmationTitle: '¿Archivar tarea programada?',
    archivedTab: 'Archivadas',
    cancel: 'Cancelar',
    cardSchedule: {
      daily: (time: string) => `Diaria a las ${time}`,
      monthly: (day: string, time: string) => `${day} a las ${time}`,
      weekdays: (time: string) => `Cada día hábil a las ${time}`,
      weekly: (day: string, time: string) => `Cada ${day} a las ${time}`,
    },
    createTask: 'Crear tarea',
    createTaskFailed: 'No se pudo crear la tarea programada.',
    createdBy: (name: string) => `Creado por ${name}`,
    datasetGroup: 'Grupo de datos',
    day: 'Día',
    delivery: 'Entrega',
    deliveryAiStudio: 'Ejecutar en AI Studio',
    deliveryAiStudioAndEmail: (email: string) =>
      `Ejecutar en AI Studio y enviar correo a ${email}`,
    editTask: 'Editar',
    editTaskAction: 'Editar tarea',
    emailResultsHelper:
      'Déjalo en blanco para conservar los resultados solo en AI Studio.',
    emailResultsTo: 'Enviar resultados por correo a',
    frequency: 'Frecuencia',
    inTimezone: (value: string, timezone: string) =>
      value.endsWith('.')
        ? `${value.slice(0, -1)} ${timezone}.`
        : `${value} ${timezone}`,
    loadDatasetGroupsFailed:
      'No se pudieron cargar los grupos de datos de tareas programadas.',
    loadTasksFailed: 'No se pudieron cargar las tareas programadas.',
    loadingRuns: 'Cargando ejecuciones de tareas',
    loadingScheduledTasks: 'Cargando tareas programadas',
    manualRun: 'Manual',
    never: 'Nunca',
    nextPage: 'Siguiente',
    noDatasetGroups:
      'Crea un grupo de datos aprobado antes de programar tareas de Studio.',
    noActiveTaskSearchResults: 'Ninguna tarea activa coincide con tu consulta.',
    noArchivedTasks:
      'No hay tareas archivadas. Archivar una tarea detiene su programación, pero conserva su historial de ejecuciones.',
    noArchivedTaskSearchResults:
      'Ninguna tarea archivada coincide con tu consulta.',
    noTasks: 'Aún no hay tareas programadas.',
    pagination: (page: number, pageCount: number) =>
      `Página ${page} de ${pageCount}`,
    pauseTask: 'Pausar',
    persistSuggestions: 'Guardar sugerencias de audiencia de cada ejecución',
    persistSuggestionsHelper:
      'Cuando Marve devuelva sugerencias de audiencia válidas, guárdalas en AI Studio para revisarlas y activarlas.',
    previousPage: 'Anterior',
    prompt: 'Instrucción',
    promptPlaceholder:
      'Describe qué debe ejecutarse en cada programación (por ejemplo, "Encuentra nuevas oportunidades de audiencia de alto valor y explica por qué vale la pena activarlas.")',
    resumeTask: 'Reanudar',
    resumeTaskAction: 'Reanudar tarea',
    runStatusLabels: {
      cancelled: 'Cancelada',
      failed: 'Fallida',
      queued: 'En cola',
      running: 'En ejecución',
      succeeded: 'Completada',
      timed_out: 'Agotó el tiempo',
    },
    runNowTask: 'Ejecutar ahora',
    runNowTaskFailed: 'No se pudo ejecutar la tarea programada.',
    runPending: 'Ejecución pendiente',
    runResultPending: 'Ejecución en progreso.',
    ranAt: 'Ejecución',
    runHistory: 'Historial de ejecuciones',
    result: 'Resultado',
    schedule: 'Programación',
    scheduledTasks: 'Tareas programadas',
    searchTasks: 'Buscar tareas',
    taskName: 'Nombre de tarea',
    taskStatusTabs: 'Estado de tareas programadas',
    noRunResult: 'No hay resumen de resultado.',
    noRuns: 'Aún no hay ejecuciones.',
    noSuccessRate: 'Aún no hay ejecuciones completadas.',
    nextRunAt: (date: string, time: string) =>
      `Próxima ejecución el ${date} a las ${time}`,
    noNextRun: 'No hay próxima ejecución programada',
    pauseTaskAction: 'Pausar tarea',
    status: 'Estado',
    successRate: (rate: number, count: number) =>
      `${rate}% de las últimas ${count} ${count === 1 ? 'ejecución' : 'ejecuciones'}`,
    successRateLabel: 'Tasa de éxito',
    tableColumns: {
      actions: 'Acciones de tarea',
      lastRun: 'Última ejecución',
      prompt: 'Instrucción',
      schedule: 'Programación',
      taskName: 'Nombre de tarea',
    },
    time: 'Hora',
    timezone: 'Zona horaria',
    tryAgain: 'Intentar de nuevo',
    unknownCreator: 'Desconocido',
    unarchiveTask: 'Desarchivar',
    updateTask: 'Actualizar tarea',
    updateTaskFailed: 'No se pudo actualizar la tarea programada.',
    viewRunHistory: 'Ver historial de ejecuciones',
    frequencyOptions: {
      hourly: 'Cada hora',
      daily: 'Diaria',
      weekdays: 'Cada día hábil',
      weekly: 'Semanal',
      monthly: 'Mensual',
      cron: 'Personalizada',
      custom: 'Personalizada',
    },
    monthlyDay: 'Día mensual',
    monthlyOptions: {
      firstDay: 'día 1 del mes',
      fifteenthDay: 'día 15 del mes',
    },
    scheduleSummary: {
      daily: (time: string) => `Se ejecuta todos los días a las ${time}.`,
      monthly: (day: string, time: string) =>
        `Se ejecuta el ${day} a las ${time}.`,
      weekdays: (time: string) => `Se ejecuta cada día hábil a las ${time}.`,
      weekly: (day: string, time: string) =>
        `Se ejecuta cada ${day} a las ${time}.`,
    },
    statusLabels: {
      active: 'Activa',
      archived: 'Archivada',
      paused: 'Pausada',
    },
    startsOn: 'Empieza el',
    weekdayOptions: {
      MO: 'lunes',
      TU: 'martes',
      WE: 'miércoles',
      TH: 'jueves',
      FR: 'viernes',
      SA: 'sábado',
      SU: 'domingo',
    },
  },
};
